export type ListingAd = {
  id: number;
  betekning?: string | null;
  muni?: string | null;
  region?: string | null;
  total_volume?: number | null;
  type: string;
  arrangement?: string | null;
  status: string;
  state: number;
  year?: number | null;
  has_ended?: boolean;
  has_bids?: boolean;
  show?: boolean;
  winner?: boolean;
  email?: string | null;
  phone?: string | null;
  owner_name?: string | null;
  owner_lastname?: string | null;
  contact?: {
    label: string;
    name: string;
    email: string;
    phone: string;
  };
  contract_pdf?: string | null;
  /** True only for the selected winning buyer when an admin-uploaded invoice exists; download via user-api (auth). */
  invoice_attachment_available?: boolean;
};

export type BidsBreakdown = {
  on_going: ListingAd[];
  won: ListingAd[];
  not_sold: ListingAd[];
  lost: ListingAd[];
};

export type AccountData = {
  ads: ListingAd[];
  bids: BidsBreakdown;
  opened: ListingAd[];
};

export type AdListingApiPayload = {
  ads: AccountData;
  years: Array<number | string>;
  child_accounts: Record<string, string>;
};

export type DropdownOption = { label: string; value: string };

export function emptyBidsBreakdown(): BidsBreakdown {
  return {
    on_going: [],
    won: [],
    not_sold: [],
    lost: [],
  };
}

/**
 * `/my-account/listings/childaccounts/` can return `bids` as `[]` when there are no bid rows.
 * `/user-api/get-ad-data/` always returns a breakdown object — normalize so UI code matches both.
 */
export function normalizeAccountData(raw: unknown): AccountData {
  if (!raw || typeof raw !== 'object') {
    return { ads: [], opened: [], bids: emptyBidsBreakdown() };
  }
  const r = raw as Record<string, unknown>;
  const ads = Array.isArray(r.ads) ? (r.ads as ListingAd[]) : [];
  const opened = Array.isArray(r.opened) ? (r.opened as ListingAd[]) : [];
  const bidsRaw = r.bids;
  if (
    bidsRaw &&
    typeof bidsRaw === 'object' &&
    !Array.isArray(bidsRaw) &&
    'on_going' in bidsRaw
  ) {
    const b = bidsRaw as BidsBreakdown;
    return {
      ads,
      opened,
      bids: {
        on_going: Array.isArray(b.on_going) ? [...b.on_going] : [],
        won: Array.isArray(b.won) ? [...b.won] : [],
        not_sold: Array.isArray(b.not_sold) ? [...b.not_sold] : [],
        lost: Array.isArray(b.lost) ? [...b.lost] : [],
      },
    };
  }
  return {
    ads,
    opened,
    bids: emptyBidsBreakdown(),
  };
}

export function filterComparisonGenerator(value: string): (ad: ListingAd) => boolean {
  if (value === 'all') {
    return () => true;
  }
  if (value === 'draft') {
    return (ad) => ad.state <= 15;
  }
  if (value === 'wfr') {
    return (ad) => ad.state === 30 || ad.state === 35;
  }
  if (value === 'no_bid') {
    return (ad) =>
      (ad.state === 40 && Boolean(ad.has_ended) && !ad.has_bids) || ad.state === 42;
  }
  if (value === 'is_over') {
    return (ad) => ad.state === 40 && Boolean(ad.has_ended);
  }
  if (value === 'live') {
    return (ad) => ad.state === 40 && !ad.has_ended;
  }
  if (value === 'no_bid_selected') {
    return (ad) => ad.state === 44;
  }
  if (value === 'bid_selected') {
    return (ad) => ad.state >= 50;
  }
  return () => true;
}

/** Whether any seller ad in `account.ads` matches the category under the current year filter (same idea as ProfileAdListings.filterOption). */
export function categoryHasMatchingAds(
  account: AccountData,
  filterYear: string,
  categoryValue: string
): boolean {
  const evalFunc = filterComparisonGenerator(categoryValue);
  const yearFilter = account.ads.filter(
    (ad) => String(ad.year) === String(filterYear) || filterYear === 'all'
  );
  return yearFilter.some((ad) => evalFunc(ad));
}

export function buildCategoryOptions(
  account: AccountData,
  filterYear: string
): DropdownOption[] {
  const options: DropdownOption[] = [{ label: 'Visa alla', value: 'all' }];
  const checks: [string, string][] = [
    ['draft', 'Utkast'],
    ['wfr', 'Väntar på granskning'],
    ['live', 'Aktiva annonser'],
    ['no_bid', 'Annonser utan anbud'],
    ['is_over', 'Välj köpare'],
    ['no_bid_selected', 'Inget anbud valt på plattformen'],
    ['bid_selected', 'Sålda annonser'],
  ];
  for (const [value, label] of checks) {
    if (categoryHasMatchingAds(account, filterYear, value)) {
      options.push({ label, value });
    }
  }
  return options;
}

export function adMatchesKeyword(ad: ListingAd, keyword: string): boolean {
  const fixedKey = keyword.toLowerCase().replace(/ /g, '');
  if (!fixedKey) return true;
  const description = ad.betekning ? ad.betekning.toLowerCase().replace(/ /g, '') : '';
  const adId = String(ad.id).replace(/ /g, '');
  const email = ad.email ? ad.email.replace(/ /g, '') : '';
  const phone = ad.phone
    ? ad.phone.replace(/ /g, '').replace(/^\+46/, '0').replace(/-/g, '')
    : '';
  const name = ad.owner_name ? ad.owner_name.toLowerCase().replace(/ /g, '') : '';
  const lastname = ad.owner_lastname ? ad.owner_lastname.toLowerCase().replace(/ /g, '') : '';
  const phoneKey = fixedKey.replace(/^\+46/, '0').replace(/-/g, '');
  return (
    description.includes(fixedKey) ||
    adId.includes(fixedKey) ||
    email.includes(fixedKey) ||
    phone.includes(phoneKey) ||
    name.includes(fixedKey) ||
    lastname.includes(fixedKey)
  );
}

function filterBidArrays(
  bids: BidsBreakdown,
  keyword: string
): BidsBreakdown {
  return {
    on_going: bids.on_going.filter((ad) => adMatchesKeyword(ad, keyword)),
    won: bids.won.filter((ad) => adMatchesKeyword(ad, keyword)),
    not_sold: bids.not_sold.filter((ad) => adMatchesKeyword(ad, keyword)),
    lost: bids.lost.filter((ad) => adMatchesKeyword(ad, keyword)),
  };
}

export function applySearchFilter(
  account: AccountData,
  keyword: string
): {
  ads: ListingAd[];
  bids: BidsBreakdown;
  opened: ListingAd[];
} {
  const k = keyword.trim();
  if (!k) {
    return {
      ads: [...account.ads],
      bids: {
        on_going: [...account.bids.on_going],
        won: [...account.bids.won],
        not_sold: [...account.bids.not_sold],
        lost: [...account.bids.lost],
      },
      opened: [...account.opened],
    };
  }
  return {
    ads: account.ads.filter((ad) => adMatchesKeyword(ad, k)),
    bids: filterBidArrays(account.bids, k),
    opened: account.opened.filter((ad) => adMatchesKeyword(ad, k)),
  };
}

/** Seller ads only — when user changes “Mina annonser” category (ProfileAdListings `filter_ads`). */
export function applyCategoryFilter(
  account: AccountData,
  filterYear: string,
  filterAds: string
): { ads: ListingAd[] } {
  const comparisonFunc = filterComparisonGenerator(filterAds);
  const yearFilter = account.ads.filter(
    (ad) => String(ad.year) === String(filterYear) || filterYear === 'all'
  );
  return {
    ads: yearFilter.filter(comparisonFunc),
  };
}

/**
 * When user changes year: reset category to `all`, filter ads/opened with year + comparison (same as legacy `filterComparisonGenerator(yearValue)` → effectively year only),
 * bids buckets get year filter only (no category).
 */
export function applyYearChange(account: AccountData, yearValue: string): {
  ads: ListingAd[];
  bids: BidsBreakdown;
  opened: ListingAd[];
  filter_year: string;
  filter_ads: string;
} {
  const comparisonFunc = filterComparisonGenerator(yearValue);
  const yearOnly = (list: ListingAd[]) =>
    list.filter((ad) => String(ad.year) === String(yearValue) || yearValue === 'all');

  return {
    filter_year: yearValue,
    filter_ads: 'all',
    ads: yearOnly(account.ads).filter(comparisonFunc),
    opened: yearOnly(account.opened).filter(comparisonFunc),
    bids: {
      on_going: yearOnly(account.bids.on_going),
      won: yearOnly(account.bids.won),
      not_sold: yearOnly(account.bids.not_sold),
      lost: yearOnly(account.bids.lost),
    },
  };
}
